import React, {useState} from 'react';
import {WorkspaceRow} from '../../store/selectors/workspace.selectors';
import {PendingTranscription} from '../../lib/pendingTranscriptions';
import {formatDuration, formatBytes} from '../../lib/audioMetadata';
import {formatRelativeDate, formatExactDate} from '../../lib/formatDate';
import {useCountdown} from '../useCountdown';
import {conversionFileName, wordListSummary} from '../conversions/ConversionRow';
import StatusChip, {WorkspaceStatus} from './StatusChip';
import {
  ChevronDownIcon,
  DownloadIcon,
  FileAudioIcon,
  FileTextIcon,
  ListIcon,
  RefreshIcon,
  TrashIcon,
  UploadCloudIcon,
  WandIcon,
} from '../Icons';

export interface AudioFileRowActions {
  onTranscribe: (row: WorkspaceRow) => void;
  onNewConversion: (row: WorkspaceRow) => void;
  onViewTranscript: (row: WorkspaceRow) => void;
  onDownloadCsv: (row: WorkspaceRow) => void;
  onDownloadConversion: (conversionId: string, fileName: string) => void;
  onDelete: (row: WorkspaceRow) => void;
  onCancelQueued: (entry: PendingTranscription) => void;
  /** Carries on a half-finished upload. Opens a file picker unless the browser remembered the file. */
  onResumeUpload: (row: WorkspaceRow) => void;
}

interface AudioFileRowProps extends AudioFileRowActions {
  row: WorkspaceRow;
  /** Blocks Transcribe when the balance cannot cover this file. */
  secondsRemaining: number;
  isQuotaKnown: boolean;
  busyConversionId: string | null;
  /** True while this row's own upload is running, so it shows progress instead of Resume. */
  isUploadingThisRow: boolean;
  /** Percent of the in-flight upload, only meaningful when `isUploadingThisRow`. */
  uploadPercent: number;
  /** Blocks Resume while some *other* file is uploading — transfers run one at a time. */
  isUploadBusy: boolean;
}

/**
 * Row state, derived rather than stored: the file and its newest transcription already say it.
 *
 * Note the order — **upload state comes first and is not transcription state**. A file that is
 * still uploading has no transcription, and a file that finished uploading may never get one:
 * "Not transcribed" is a resting state, not a to-do.
 */
function deriveStatus(row: WorkspaceRow, isUploadingThisRow: boolean): {
  status: WorkspaceStatus;
  label: string;
  busy: boolean;
} {
  // A `pending` row is an upload with parts still to go, not a dead one — R2 remembers what landed,
  // so this is a state to carry on from. The wording avoids "incomplete" and "failed" for that
  // reason: nothing here is broken, and the row's own button is the way out of it.
  if (row.audio.status === 'pending') {
    return isUploadingThisRow
      ? {status: 'uploading', label: 'Uploading…', busy: true}
      : {status: 'incomplete', label: 'Upload unfinished', busy: false};
  }

  const transcription = row.activeTranscription;

  // Queued outranks "not transcribed" but never outranks a real job: a file can be queued for a
  // second pass while an earlier transcription is still running or already usable, and hiding that
  // behind the wait would lose the CSV the user already has.
  if (!transcription || transcription.status === 'failed' || transcription.status === 'rejected') {
    if (row.queued) {
      return {status: 'queued', label: 'Queued', busy: false};
    }
  }

  if (!transcription) {
    return {status: 'not-transcribed', label: 'Not transcribed', busy: false};
  }
  if (transcription.status === 'processing') {
    return {status: 'transcribing', label: 'Transcribing…', busy: true};
  }
  if (transcription.status === 'failed') {
    return {status: 'failed', label: 'Failed', busy: false};
  }
  // Terminal like 'failed' — there is no CSV in R2 — but the seconds are not refunded, so it gets
  // its own label rather than being folded into "Failed".
  if (transcription.status === 'rejected') {
    return {status: 'rejected', label: 'Rejected', busy: false};
  }

  return {status: 'ready', label: 'Ready', busy: false};
}

const linkButton = 'text-xs text-gray-400 hover:text-gray-100 transition-colors cursor-pointer '
  + 'disabled:opacity-40 disabled:cursor-not-allowed';

/**
 * One audio file and everything that came out of it.
 *
 * The desktop app spread this across three screens — upload, transcribe, convert — and left the
 * user to remember which CSV came from which recording. Here the file is the unit of work and its
 * transcription and conversions are attributes of it, so the next action is always in the row.
 */
const AudioFileRow: React.FC<AudioFileRowProps> = ({
  row,
  secondsRemaining,
  isQuotaKnown,
  busyConversionId,
  isUploadingThisRow,
  uploadPercent,
  isUploadBusy,
  onTranscribe,
  onNewConversion,
  onViewTranscript,
  onDownloadCsv,
  onDownloadConversion,
  onDelete,
  onCancelQueued,
  onResumeUpload,
}) => {
  const {audio, activeTranscription, conversions, queued} = row;
  const {status, label, busy} = deriveStatus(row, isUploadingThisRow);
  const startsIn = useCountdown(status === 'queued' && queued ? queued.releaseAt : null);
  const [conversionsExpanded, setConversionsExpanded] = useState(false);
  const hasConversions = conversions.length > 0;

  // Once a transcription exists, its duration is the settled one — AssemblyAI's real measurement,
  // which can differ from what mediaInfo.js probed and we submitted at upload time. Prefer it over
  // the upload's own duration so the row stops showing the number we sent once a truer one exists.
  const duration = activeTranscription?.duration ?? audio.duration ?? 0;
  const knownSize = audio.sizeBytes ?? audio.clientSizeBytes;
  const overBalance = isQuotaKnown && duration > 0 && duration > secondsRemaining;

  const toggleConversions = () => setConversionsExpanded(value => !value);

  // Nothing else in the row is clickable, so the row itself opens the conversions panel. Every real
  // button stops propagation so it keeps doing its own thing instead of also toggling the panel. The
  // hover highlight is gated on hasConversions too — a row with nothing to open shouldn't look like it does.
  return (
    <div
      className={`group grid grid-cols-12 items-center gap-4 px-4 py-3 border-b border-gray-800/70
       last:border-b-0 transition-colors ${hasConversions ? 'cursor-pointer hover:bg-gray-800/30' : ''}`}
      onClick={hasConversions ? toggleConversions : undefined}
    >
      {/* File */}
      <div className="col-span-12 md:col-span-4 flex items-start gap-3 min-w-0">
        <FileAudioIcon className="w-4 h-4 mt-0.5 shrink-0 text-gray-600" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-100 truncate" title={audio.name}>{audio.name}</p>
          <p className="text-xs text-gray-500">
            {/* `sizeBytes` is only written once R2 confirms what it received, so a half-finished
                upload has nothing but the size the browser reported when it started. */}
            {knownSize ? formatBytes(knownSize) : 'Size unknown'}
            {audio.sampleRate ? ` · ${(audio.sampleRate / 1000).toFixed(1)} kHz` : ''}
          </p>
        </div>
      </div>

      {/* Duration */}
      <div className="col-span-4 md:col-span-1 text-xs tabular-nums text-gray-400">
        {duration > 0 ? formatDuration(duration) : '—'}
      </div>

      {/* Uploaded */}
      <div className="col-span-8 md:col-span-2 text-xs text-gray-400" title={formatExactDate(audio.createdAt)}>
        {formatRelativeDate(audio.createdAt)}
      </div>

      {/* Status — upload state until the file has landed, transcription state after */}
      <div className="col-span-6 md:col-span-2 min-w-0">
        <StatusChip status={status} label={label} busy={busy} />
        {status === 'transcribing' && (
          <p className="mt-1 text-xs text-gray-500">Usually 30 seconds to a few minutes</p>
        )}
        {status === 'queued' && (
          <p className="mt-1 text-xs text-gray-500">
            {startsIn !== null && startsIn > 0
              ? `Starts in ${formatDuration(startsIn)}`
              : 'Starting shortly'}
          </p>
        )}
        {(status === 'failed' || status === 'rejected') && activeTranscription?.error && (
          <p className="mt-1 text-xs text-red-400/80 truncate" title={activeTranscription.error}>
            {activeTranscription.error}
          </p>
        )}
        {status === 'incomplete' && (
          <p className="mt-1 text-xs text-gray-500">Pick the same file to carry on</p>
        )}
        {status === 'uploading' && (
          <p className="mt-1 text-xs tabular-nums text-gray-500">{uploadPercent}%</p>
        )}
      </div>

      {/* Conversions */}
      <div className="col-span-6 md:col-span-1 min-w-0">
        {conversions.length === 0 ? (
          <span className="text-xs text-gray-600">—</span>
        ) : (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              toggleConversions();
            }}
            aria-expanded={conversionsExpanded}
            title={
              conversionsExpanded
                ? 'Hide conversions'
                : `Show ${conversions.length} conversion${conversions.length === 1 ? '' : 's'}`
            }
            className="inline-flex items-center gap-1 rounded bg-gray-800 px-1.5 py-0.5 text-xs
             text-gray-300 hover:bg-gray-700 hover:text-white transition-colors cursor-pointer"
          >
            <ListIcon className="w-3 h-3 shrink-0" />
            <span className="tabular-nums">{conversions.length}</span>
            <ChevronDownIcon
              className={`w-3 h-3 shrink-0 transition-transform ${conversionsExpanded ? 'rotate-180' : ''}`}
            />
          </button>
        )}
      </div>

      {/* Actions */}
      <div className="col-span-12 md:col-span-2 flex items-center justify-start md:justify-end gap-3">
        {status === 'ready' && (
          <>
            <div className="flex flex-col items-end gap-1">
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onNewConversion(row);
                }}
                className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-2.5 py-1.5
                 text-xs font-medium text-white hover:bg-indigo-500 transition-colors cursor-pointer"
              >
                <WandIcon className="w-3.5 h-3.5" />
                New conversion
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className={linkButton}
                  onClick={(event) => {
                    event.stopPropagation();
                    onViewTranscript(row);
                  }}
                >
                  View transcript
                </button>
                <span className="text-gray-700">·</span>
                <button
                  type="button"
                  className={linkButton}
                  onClick={(event) => {
                    event.stopPropagation();
                    onDownloadCsv(row);
                  }}
                >
                  Download CSV
                </button>
              </div>
            </div>
          </>
        )}

        {status === 'queued' && queued && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onCancelQueued(queued);
            }}
            title={
              queued.reason === 'in_flight'
                ? 'Stop waiting for a slot to free up — nothing has been spent'
                : 'Stop waiting for the session reset — nothing has been spent'
            }
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-700 px-2.5 py-1.5
             text-xs font-medium text-gray-200 hover:border-gray-600 hover:bg-gray-800
             transition-colors cursor-pointer"
          >
            Cancel queued
          </button>
        )}

        {status === 'incomplete' && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onResumeUpload(row);
            }}
            disabled={isUploadBusy}
            title={
              isUploadBusy
                ? 'Another file is uploading — one at a time'
                : 'Carries on from where it stopped. Only the parts that never arrived are sent again.'
            }
            className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-2.5 py-1.5
             text-xs font-medium text-white hover:bg-indigo-500 transition-colors
             disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
          >
            <UploadCloudIcon className="w-3.5 h-3.5" />
            Resume
          </button>
        )}

        {status === 'not-transcribed' && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onTranscribe(row);
            }}
            disabled={overBalance}
            title={
              overBalance
                ? 'This file is longer than your remaining transcription time'
                : 'Uses your transcription quota'
            }
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-700 px-2.5 py-1.5
             text-xs font-medium text-gray-200 hover:border-gray-600 hover:bg-gray-800 transition-colors
             disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
          >
            <FileTextIcon className="w-3.5 h-3.5" />
            Transcribe
          </button>
        )}

        {(status === 'failed' || status === 'rejected') && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onTranscribe(row);
            }}
            title={
              status === 'rejected'
                ? 'Retries the same file — nothing is uploaded again. The seconds from this attempt '
                  + 'were not refunded, so a retry only helps if the file now fits your plan.'
                : 'Retries the same file — nothing is uploaded again'
            }
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-700 px-2.5 py-1.5
             text-xs font-medium text-gray-200 hover:border-gray-600 hover:bg-gray-800 transition-colors cursor-pointer"
          >
            <RefreshIcon className="w-3.5 h-3.5" />
            Retry
          </button>
        )}

        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onDelete(row);
          }}
          aria-label={`Delete ${audio.name}`}
          title="Delete this audio file"
          className="p-1.5 rounded-lg text-gray-600 hover:text-red-300 hover:bg-gray-800 transition-colors
           opacity-0 group-hover:opacity-100 focus:opacity-100 cursor-pointer"
        >
          <TrashIcon className="w-4 h-4" />
        </button>
      </div>

      {/* Conversions detail — full-width so each word list's full name has room to breathe */}
      {conversionsExpanded && conversions.length > 0 && (
        <div className="col-span-12 space-y-1.5 rounded-lg border border-gray-800 bg-gray-950/40 p-3">
          {conversions.map((conversion) => {
            const fileName = conversionFileName(conversion);
            // Every list that went into it, not just the first — with the full set on hover.
            const wordLists = wordListSummary(conversion);
            const wordListLabel = wordLists?.label ?? 'XML';

            return (
              <div key={conversion.id} className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                  <ListIcon className="w-3.5 h-3.5 shrink-0 text-gray-600" />
                  <div className="min-w-0">
                    <p className="text-xs text-gray-200 truncate" title={wordLists?.title ?? wordListLabel}>
                      {wordListLabel}
                    </p>
                    <p className="text-[11px] text-gray-500 truncate" title={fileName}>{fileName}</p>
                  </div>
                </div>
                <button
                  type="button"
                  disabled={busyConversionId === conversion.id}
                  onClick={(event) => {
                    event.stopPropagation();
                    onDownloadConversion(conversion.id, fileName);
                  }}
                  title={`Download ${fileName}`}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-gray-800 px-2 py-1
                   text-xs text-gray-300 hover:bg-gray-700 hover:text-white transition-colors
                   disabled:opacity-50 cursor-pointer"
                >
                  <DownloadIcon className="w-3.5 h-3.5" />
                  Download
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AudioFileRow;
